<template>
  <Modal
    ref="requestUpdateModalRef"
    :showIcon="false"
    :clickToClose="true"
    class="request-update-modal"
    :modalStyle="{
      height: '600px',
    }"
    positiveActionText="Talep Oluştur"
    negativeActionText="Vazgeç"
    :isPositiveActionButtonDisabled="!isFormValid || isSubmitting"
    @onPositiveActionClick="handleSubmit"
    @onNegativeActionClick="handleClose"
    @modalClosed="handleModalClosed"
  >
    <template v-slot:body>
      <div class="header">
        <p class="title">
          Ürün Açıklaması için Güncelleme Talep Et/Hata Bildir
        </p>
      </div>

      <div class="scrollable-content">
        <div class="form-container">
          <div class="two-column-grid">
            <div class="column">
              <h3 class="column-title">Mevcut Ürün Açıklaması</h3>
              <div class="description-container">
                <div class="current-description">
                  <div class="current-description-header">
                    <button class="copy-button" @click="copyCurrentDescription">
                      <Icons name="Copy" size="xxs" color="#1C1C1E" />
                      <span>İçeriği Kopyala</span>
                    </button>
                  </div>
                  <div class="current-description-content" v-html="currentProductDescription"></div>
                </div>
              </div>
            </div>
            <div class="column">
              <h3 class="column-title">Talep Ettiğiniz Ürün Açıklaması</h3>
              <div class="fields-container">
                <div class="rich-text-container">
                  <JoditEditor
                    v-model="requestedProductDescription"
                    placeholder="Yeni açıklama metnini yazın"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { Modal, Icons, JoditEditor } from "@n11/so-ui-components";
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';
import { sanitizeRichTextHtmlForApi } from '@/utils/sanitizeRichTextHtmlForApi';

const store = useStore();
const requestUpdateModalRef = ref(null);

const isSubmitting = ref(false);
const skipDismissOnClose = ref(false);

const productDetail = computed(() => store.getters['products/getProductDetail']);
const productDescription = computed(() => productDetail.value?.description || '');

const currentProductDescription = ref("");
const requestedProductDescription = ref("");

const showCopySuccessToast = () => {
  store.commit("common/SET_TOASTER_DATA", {
    type: "success",
    title: "Başarılı",
    description: "İçerik panoya kopyalandı.",
  });
};

const copyCurrentDescription = async () => {
  try {
    const htmlContent = currentProductDescription.value || '';
    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([htmlContent], { type: 'text/html' }),
      'text/plain': new Blob([htmlContent.replace(/<[^>]*>/g, '')], { type: 'text/plain' })
    });
    await navigator.clipboard.write([clipboardItem]);
    showCopySuccessToast();
  } catch (error) {
    console.error('Kopyalama hatası:', error);
  }
};

const isFormValid = computed(() => {
  if (!requestedProductDescription.value) return false;
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = requestedProductDescription.value;
  const plainText = tempDiv.textContent || tempDiv.innerText || '';
  return plainText.trim().length > 0;
});

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;
  
  isSubmitting.value = true;

  try {
    const descriptionHtml =
      sanitizeRichTextHtmlForApi(requestedProductDescription.value.trim()) ??
      '';
    const requestData = {
      groupId: productDetail.value?.groupId || null,
      description: descriptionHtml,
    };

    await store.dispatch("products/submitProductDescriptionUpdateRequest", requestData);
    sendProductDetailEvent(GA4_EVENT.REQUEST_UPDATE_REPORT_BUG_SUCCESS, {
      button_name: 'Talep Oluştur',
      button_type: 'Request Success',
      button_area: 'Product Description',
      item_id: productDetail.value?.id,
    });

    store.commit("common/SET_TOASTER_DATA", {
      type: "success",
      title: "Başarılı",
      description: "Talep başarıyla oluşturuldu",
    });

    skipDismissOnClose.value = true;
    handleClose();
  } catch (error) {
    const errorMessage = error.errors?.[0]?.message || "Bir hata oluştu. Lütfen tekrar deneyin.";

    store.commit("common/SET_TOASTER_DATA", {
      type: "error",
      title: "Hata",
      description: errorMessage,
    });
  } finally {
    isSubmitting.value = false;
  }
};

const handleClose = () => {
  resetForm();
  requestUpdateModalRef.value?.hiddenModal();
};

const handleModalClosed = () => {
  if (!skipDismissOnClose.value) {
    sendProductDetailEvent(GA4_EVENT.REQUEST_UPDATE_REPORT_BUG_DISMISS, {
      button_name: 'Vazgeç',
      button_type: 'Request Dismiss',
      button_area: 'Product Description',
      item_id: productDetail.value?.id,
    });
  }
  skipDismissOnClose.value = false;
};

const resetForm = () => {
  requestedProductDescription.value = "";
  currentProductDescription.value = "";
  isSubmitting.value = false;
};

const openModal = (source) => {
  resetForm();
  currentProductDescription.value = productDescription.value || '';
  requestUpdateModalRef.value?.openModal();
};

defineExpose({
  openModal,
});
</script>

<style scoped lang="scss">
@import "@/assets/styles/variables.scss";

.request-update-modal {
  :deep(.modal-container) {
    display: flex;
    width: 1300px;
    padding: 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }

  :deep(.modal-header) {
    position: absolute;
    top: 7px;
    right: 4px;
  }

  :deep(.modal-body) {
    padding: 0 !important;
    width: 100%;
    height: 100%;
    overflow: hidden !important;

    .content {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden !important;
    }
  }

  :deep(.modal-footer) {
    width: 100%;
    padding: 24px 12px 0 0 !important;
    margin-top: 0 !important;
  }

  // Jodit Editor Styles
  :deep(.jodit-editor-wrapper) {
    width: 100%;

    .jodit-container {
      border: none !important;
      height: 100% !important;
    }

    .jodit-toolbar__box {
      border-radius: 6px 6px 0 0 !important;
      padding: 4px 0 !important;
    }

    .jodit-workplace {
      min-height: 250px !important;
    }

    .jodit-wysiwyg {
      min-height: 250px !important;
      font-size: 14px !important;
      line-height: 1.6 !important;
      color: $colorN80 !important;
      padding: 16px 16px 24px 16px !important;
      text-align: left !important;
      word-break: break-word;

      &::-webkit-scrollbar {
        width: 6px !important;
      }

      &::-webkit-scrollbar-track {
        background: $colorN10 !important;
        border-radius: 3px !important;
      }

      &::-webkit-scrollbar-thumb {
        background: $colorN40 !important;
        border-radius: 3px !important;

        &:hover {
          background: $colorN50 !important;
        }
      }
    }
  }
}

.header {
  margin-bottom: 24px;
  .title {
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.2px;
    text-align: left;
  }
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: $colorN10;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: $colorN40;
    border-radius: 3px;

    &:hover {
      background: $colorN50;
    }
  }
}

.two-column-grid {
  display: flex;
  gap: 30px;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: $colorN20;
    transform: translateX(-50%);
  }

  .column {
    flex: 0 0 calc(50% - 15px);
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .column-title {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: -0.05px;
      color: $colorN80;
      margin-bottom: 4px;
      text-align: left;
    }

    &:nth-child(1) .column-title,
    &:nth-child(2) .column-title {
      color: $colorN80;
      font-size: 13px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
    }

    .description-container {
      height: 100%;

      .current-description {
        border-radius: 8px;
        border: 1px solid $colorN20;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        min-height: 360px;
        height: 100%;
        
        .current-description-header {
          display: flex;
          justify-content: flex-end;
          padding: 8px 16px;
          background: $colorN20;
          .copy-button {
            display: flex;
            align-items: center;
            gap: 6px;
            cursor: pointer;
            transition: all 0.2s;
            border-radius: 4px;
            border: 1px solid $colorN80;
            background: $colorN00;
            color: $colorN80;
            font-size: 13px;
            font-weight: 700;
            line-height: 18px;
            letter-spacing: -0.05px;
            padding: 7px 16px;
          }
        }
        
        .current-description-content {
          padding: 16px 16px 24px 16px;
          overflow-y: auto;
          font-size: 14px;
          line-height: 1.6;
          color: $colorN80;
          text-align: left;
          &::-webkit-scrollbar {
            width: 6px;
          }

          &::-webkit-scrollbar-track {
            background: $colorN10;
            border-radius: 3px;
          }

          &::-webkit-scrollbar-thumb {
            background: $colorN40;
            border-radius: 3px;

            &:hover {
              background: $colorN50;
            }
          }
        }
      }
    }

    .fields-container {
      display: flex;
      flex-direction: column;
      gap: 16px;
      background-color: $colorBe20;
      border-radius: 8px;
      flex: 1;
      :deep(.input-container) {
        margin-bottom: 0;
      }

      .rich-text-container {
        display: flex;
        flex-direction: column;
        background-color: $colorN00;
        border-radius: 8px;
        border: 1px solid $colorN20;
        overflow: hidden;
        min-height: 360px;
        height: 100%;
      }
    }
  }
}
</style>

